import { useEffect, useState } from "react";
import {
  addEventImage,
  createEvent,
  deleteEvent,
  removeEventImage,
  subscribeToEvents,
  updateEvent,
} from "../../services/eventsService";

const initialEventState = {
  titleHindi: "",
  titleEnglish: "",
  descriptionHindi: "",
  descriptionEnglish: "",
  eventDate: "",
};

const formatDateInput = (value) => {
  if (!value) {
    return "";
  }
  if (typeof value === "string") {
    return value.slice(0, 10);
  }
  if (value.toDate) {
    const date = value.toDate();
    return date.toISOString().slice(0, 10);
  }
  if (value instanceof Date) {
    return value.toISOString().slice(0, 10);
  }
  return new Date(value).toISOString().slice(0, 10);
};

const toDate = (value) => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return value;
  }
  if (value.toDate) {
    return value.toDate();
  }
  return new Date(value);
};

const EventsManager = () => {
  const [events, setEvents] = useState([]);
  const [eventForm, setEventForm] = useState(initialEventState);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [imageForms, setImageForms] = useState({});

  useEffect(() => {
    const unsubscribe = subscribeToEvents((items) => setEvents(items));
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, []);

  const resetForm = () => {
    setEventForm(initialEventState);
    setEditingId(null);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setEventForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const payload = {
        ...eventForm,
        eventDate: eventForm.eventDate ? new Date(eventForm.eventDate) : null,
      };
      if (editingId) {
        await updateEvent(editingId, payload);
        setMessage("आयोजन अद्यतन हुआ। / Event updated.");
      } else {
        await createEvent(payload);
        setMessage("नया आयोजन जोड़ा गया। / Event created.");
      }
      resetForm();
    } catch (error) {
      setMessage(`त्रुटि: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (eventItem) => {
    setEditingId(eventItem.id);
    setEventForm({
      titleHindi: eventItem.titleHindi || "",
      titleEnglish: eventItem.titleEnglish || "",
      descriptionHindi: eventItem.descriptionHindi || "",
      descriptionEnglish: eventItem.descriptionEnglish || "",
      eventDate: formatDateInput(eventItem.eventDate),
    });
  };

  const handleImageInput = (eventId, field, value) => {
    setImageForms((prev) => ({
      ...prev,
      [eventId]: {
        ...(prev[eventId] || {
          captionHindi: "",
          captionEnglish: "",
          file: null,
        }),
        [field]: value,
      },
    }));
  };

  const submitImage = async (eventId) => {
    const form = imageForms[eventId];
    if (!form || !form.file) {
      setMessage(
        "कृपया आयोजन हेतु चित्र चुनें। / Please select an image to upload.",
      );
      return;
    }
    setSaving(true);
    setMessage("");
    try {
      await addEventImage(eventId, form);
      setImageForms((prev) => ({
        ...prev,
        [eventId]: { captionHindi: "", captionEnglish: "", file: null },
      }));
      setMessage("आयोजन चित्र जोड़ा गया। / Event image added.");
    } catch (error) {
      setMessage(`त्रुटि: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveImage = async (eventId, image) => {
    if (
      window.confirm(
        "क्या आप आयोजन का यह चित्र हटाना चाहते हैं? / Remove this event image?",
      )
    ) {
      setSaving(true);
      try {
        await removeEventImage(eventId, image.id);
        setMessage("चित्र हटाया गया। / Image removed.");
      } catch (error) {
        setMessage(`त्रुटि: ${error.message}`);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (
      window.confirm(
        "क्या आप इस आयोजन को हटाना चाहते हैं? / Delete this event?",
      )
    ) {
      setSaving(true);
      try {
        await deleteEvent(eventId);
        setMessage("आयोजन हटाया गया। / Event deleted.");
        if (editingId === eventId) {
          resetForm();
        }
      } catch (error) {
        setMessage(`त्रुटि: ${error.message}`);
      } finally {
        setSaving(false);
      }
    }
  };

  return (
    <section className="admin-section">
      <header>
        <h3>आयोजन प्रबंधन</h3>
        <span className="caption">Events Management</span>
      </header>

      <form onSubmit={handleSubmit} className="admin-form">
        <label htmlFor="event-title-hi">
          शीर्षक (हिंदी)
          <span className="caption">Title (Hindi)</span>
        </label>
        <input
          id="event-title-hi"
          name="titleHindi"
          value={eventForm.titleHindi}
          onChange={handleInputChange}
          required
        />

        <label htmlFor="event-title-en">
          शीर्षक (अंग्रेज़ी)
          <span className="caption">Title (English)</span>
        </label>
        <input
          id="event-title-en"
          name="titleEnglish"
          value={eventForm.titleEnglish}
          onChange={handleInputChange}
          required
        />

        <label htmlFor="event-desc-hi">
          विवरण (हिंदी)
          <span className="caption">Description (Hindi)</span>
        </label>
        <textarea
          id="event-desc-hi"
          name="descriptionHindi"
          value={eventForm.descriptionHindi}
          onChange={handleInputChange}
          rows={4}
          required
        />

        <label htmlFor="event-desc-en">
          विवरण (अंग्रेज़ी)
          <span className="caption">Description (English)</span>
        </label>
        <textarea
          id="event-desc-en"
          name="descriptionEnglish"
          value={eventForm.descriptionEnglish}
          onChange={handleInputChange}
          rows={4}
          required
        />

        <label htmlFor="event-date">
          आयोजन तिथि
          <span className="caption">Event Date</span>
        </label>
        <input
          id="event-date"
          name="eventDate"
          type="date"
          value={eventForm.eventDate}
          onChange={handleInputChange}
          required
        />

        <div className="form-actions">
          <button type="submit" disabled={saving}>
            {editingId ? "आयोजन अद्यतन करें" : "नया आयोजन जोड़ें"}
            <span className="caption">
              {editingId ? "Update Event" : "Add Event"}
            </span>
          </button>
          {editingId && (
            <button type="button" onClick={resetForm}>
              निरस्त करें
              <span className="caption">Cancel</span>
            </button>
          )}
        </div>
        {message && <p className="form-message">{message}</p>}
      </form>

      <div className="admin-list">
        {events.map((eventItem) => {
          const eventDate = toDate(eventItem.eventDate);
          const imageForm = imageForms[eventItem.id] || {
            captionHindi: "",
            captionEnglish: "",
            file: null,
          };
          return (
            <article key={eventItem.id} className="admin-card">
              <header>
                <h4>{eventItem.titleHindi}</h4>
                <span className="caption">{eventItem.titleEnglish}</span>
                <p className="caption">
                  {eventDate ? eventDate.toDateString() : ""}
                </p>
              </header>
              <p>{eventItem.descriptionHindi}</p>
              <span className="caption">{eventItem.descriptionEnglish}</span>
              <div className="card-actions">
                <button type="button" onClick={() => handleEdit(eventItem)}>
                  संपादन
                  <span className="caption">Edit</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteEvent(eventItem.id)}
                  className="danger"
                >
                  हटाएँ
                  <span className="caption">Delete</span>
                </button>
              </div>

              <div className="event-image-manager">
                <h5>चित्र जोड़ें</h5>
                <span className="caption">Add Images</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) =>
                    handleImageInput(
                      eventItem.id,
                      "file",
                      e.target.files ? e.target.files[0] : null,
                    )
                  }
                />
                <input
                  placeholder="कैप्शन (हिंदी)"
                  value={imageForm.captionHindi}
                  onChange={(e) =>
                    handleImageInput(
                      eventItem.id,
                      "captionHindi",
                      e.target.value,
                    )
                  }
                />
                <input
                  placeholder="Caption (English)"
                  value={imageForm.captionEnglish}
                  onChange={(e) =>
                    handleImageInput(
                      eventItem.id,
                      "captionEnglish",
                      e.target.value,
                    )
                  }
                />
                <button
                  type="button"
                  onClick={() => submitImage(eventItem.id)}
                  disabled={saving}
                >
                  चित्र अपलोड करें
                  <span className="caption">Upload Image</span>
                </button>
              </div>

              {eventItem.images && eventItem.images.length > 0 && (
                <div className="event-image-list">
                  {eventItem.images.map((image) => (
                    <div key={image.id} className="event-image-item">
                      <img
                        src={image.url}
                        alt={image.captionEnglish || eventItem.titleEnglish}
                      />
                      <div>
                        <span>{image.captionHindi}</span>
                        <span className="caption">{image.captionEnglish}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(eventItem.id, image)}
                        className="danger"
                      >
                        हटाएँ
                        <span className="caption">Remove</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </article>
          );
        })}
        {events.length === 0 && <p>आयोजनों की सूची खाली है।</p>}
      </div>
    </section>
  );
};

export default EventsManager;
